/**
 * Cloudflare Image Transformations.
 *
 * Images stay in R2 and are resized/re-encoded at the edge by URL, rather
 * than being uploaded into a Cloudflare Images bucket. That distinction is
 * what makes this free: transformations on images from R2 or an external
 * origin get 5,000 unique transformations a month at no cost, while the
 * per-image storage and delivery fees apply only to the Images bucket.
 *
 * A "unique transformation" is one image plus one set of options, counted
 * monthly — repeat views are free, so the cost is bounded by
 * (images x variants), not by traffic. At ~60 icons and a handful of widths
 * that is a few hundred against the allowance.
 *
 * The legacy icons are 400 KB on average because nothing ever resized them;
 * `format=auto` (AVIF/WebP by negotiation) plus a real width takes that to
 * roughly 15-30 KB without touching the stored object.
 */

/** Widths generated for `srcset`. Kept short — each one costs a transformation. */
const SRCSET_WIDTHS = [320, 640, 960, 1280] as const;

export interface TransformOptions {
  /** Gaussian blur radius, 1-250. Used for placeholders. */
  blur?: number;
  /** Output width in pixels. Omit to keep the original. */
  width?: number;
  /** 1-100. Defaults to Cloudflare's own default when omitted. */
  quality?: number;
}

/**
 * Only our own asset origin is transformed. An `imagedelivery.net` URL is
 * already a delivery URL, and anything else is a third party we should not
 * be proxying.
 *
 * Gated behind `VITE_IMAGE_TRANSFORMS` because `/cdn-cgi/image/...` 404s
 * unless Image Transformations are enabled for the zone — and a 404 here is
 * not a degraded image, it is a blank one. Shipping the rewrite before the
 * zone setting landed blanked every image on the site. Off by default: the
 * flag is turned on once the setting is confirmed live, so the failure mode
 * is "unoptimised images" rather than "no images".
 */
const isTransformable = (src: string) => {
  if (import.meta.env.VITE_IMAGE_TRANSFORMS !== "on") {
    return false;
  }
  const origin = import.meta.env.VITE_ASSET_ORIGIN;
  return Boolean(origin) && src.startsWith(`${origin}/`);
};

const optionString = (options: TransformOptions) => {
  const parts = ["format=auto"];
  if (options.width) {
    parts.push(`width=${options.width}`);
  }
  if (options.quality) {
    parts.push(`quality=${options.quality}`);
  }
  if (options.blur) {
    parts.push(`blur=${options.blur}`);
  }
  return parts.join(",");
};

/**
 * Rewrites an asset URL through the transformation endpoint. Returns the
 * input unchanged when transformation does not apply, so callers can pass
 * any URL without checking first.
 */
export const cdnImageUrl = (src: string, options: TransformOptions = {}) =>
  isTransformable(src) ? `/cdn-cgi/image/${optionString(options)}/${src}` : src;

/**
 * `srcset` across {@link SRCSET_WIDTHS}, letting the browser pick by device
 * pixel ratio and layout width. Widths above the source resolution are
 * skipped by Cloudflare rather than upscaled.
 */
export const cdnImageSrcSet = (src: string, options: TransformOptions = {}) =>
  isTransformable(src)
    ? SRCSET_WIDTHS.map(
        (width) => `${cdnImageUrl(src, { ...options, width })} ${width}w`
      ).join(", ")
    : undefined;
